<script setup lang="ts">
const props = withDefaults(defineProps<{
  type?: 'success' | 'error' | 'warning' | 'info'
  variant?: 'flat' | 'outlined' | 'tonal' | 'text'
  closable?: boolean
  icon?: string | boolean
  density?: 'default' | 'comfortable' | 'compact'
  border?: 'start' | 'end' | 'top' | 'bottom' | false
  color?: string
  prominent?: boolean
  title?: string
  modelValue?: boolean
}>(), {
  type: 'info',
  variant: 'tonal',
  modelValue: true,
  density: 'default',
  border: false,
})

const emit = defineEmits<{
  'update:modelValue': [val: boolean]
}>()

const visible = ref(props.modelValue)
watch(() => props.modelValue, v => visible.value = v)

const typeIcons: Record<string, string> = {
  success: 'check-circle',
  error: 'alert-circle',
  warning: 'alert',
  info: 'information',
}

const effectiveColor = computed(() => props.color || props.type)
</script>

<template>
  <div
    v-if="visible"
    class="m-alert"
    :class="[
      `m-alert--${variant}`,
      `m-alert--${effectiveColor}`,
      `m-alert--${density}`,
      {
        'm-alert--prominent': prominent,
        [`m-alert--border-${border}`]: border,
      }
    ]"
    role="alert"
  >
    <MIcon
      v-if="icon !== false"
      :icon="typeof icon === 'string' ? icon : typeIcons[type]"
      :size="prominent ? 'large' : 'default'"
      class="m-alert__icon"
    />
    <div class="m-alert__body">
      <div v-if="title" class="m-alert__title">{{ title }}</div>
      <div class="m-alert__text"><slot /></div>
    </div>
    <button v-if="closable" class="m-alert__close" @click="visible = false; emit('update:modelValue', false)">
      <MIcon icon="close" size="small" />
    </button>
  </div>
</template>

<style scoped>
.m-alert {
  display: flex;
  align-items: flex-start;
  gap: var(--m-space-3);
  padding: var(--m-space-4);
  border-radius: var(--m-radius);
  position: relative;
}

.m-alert--compact { padding: var(--m-space-2) var(--m-space-3); }
.m-alert--comfortable { padding: var(--m-space-3); }

/* Tonal variants */
.m-alert--tonal.m-alert--success { background: color-mix(in srgb, var(--m-success) 12%, transparent); color: var(--m-success); }
.m-alert--tonal.m-alert--error { background: color-mix(in srgb, var(--m-error) 12%, transparent); color: var(--m-error); }
.m-alert--tonal.m-alert--warning { background: color-mix(in srgb, var(--m-warning) 12%, transparent); color: var(--m-warning); }
.m-alert--tonal.m-alert--info { background: color-mix(in srgb, var(--m-info) 12%, transparent); color: var(--m-info); }

/* Flat variants */
.m-alert--flat.m-alert--success { background: var(--m-success); color: #fff; }
.m-alert--flat.m-alert--error { background: var(--m-error); color: #fff; }
.m-alert--flat.m-alert--warning { background: var(--m-warning); color: #333; }
.m-alert--flat.m-alert--info { background: var(--m-info); color: #fff; }

/* Outlined variants */
.m-alert--outlined.m-alert--success { border: 1px solid var(--m-success); color: var(--m-success); }
.m-alert--outlined.m-alert--error { border: 1px solid var(--m-error); color: var(--m-error); }
.m-alert--outlined.m-alert--warning { border: 1px solid var(--m-warning); color: var(--m-warning); }
.m-alert--outlined.m-alert--info { border: 1px solid var(--m-info); color: var(--m-info); }

/* Text variant */
.m-alert--text { background: transparent; }
.m-alert--text.m-alert--success { color: var(--m-success); }
.m-alert--text.m-alert--error { color: var(--m-error); }
.m-alert--text.m-alert--warning { color: var(--m-warning); }
.m-alert--text.m-alert--info { color: var(--m-info); }

/* Border variants */
.m-alert--border-start { border-left: 4px solid currentColor; }
.m-alert--border-end { border-right: 4px solid currentColor; }
.m-alert--border-top { border-top: 4px solid currentColor; }
.m-alert--border-bottom { border-bottom: 4px solid currentColor; }

.m-alert__icon { flex-shrink: 0; margin-top: 2px; }
.m-alert__body { flex: 1; min-width: 0; }
.m-alert__title { font-weight: 600; margin-bottom: var(--m-space-1); font-size: var(--m-font-size-base); }
.m-alert__text { font-size: var(--m-font-size-sm); }

.m-alert__close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  padding: var(--m-space-1);
}
.m-alert__close:hover { opacity: 1; }
</style>
